import { ethers } from "ethers";
import { usesPrivateFees, executePrivateFeeSpend } from "./privateFees";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { fail } from "./common";
import type {
  FlatJoinSplitV2MigrateParams,
  FlatJoinSplitV2OperationResult,
} from "./operationTypes";
import {
  authenticateAndDedupeCandidates,
  candidateNotes,
} from "./noteSelection";
import {
  readCurrentTokenDomain,
  requireEnabled,
  requireProvider,
} from "./poolAccess";
import { proveAndSend } from "./proveAndSend";
import { selfDeliveries } from "./witness";

export async function executeFlatJoinSplitV2Migration(
  params: FlatJoinSplitV2MigrateParams,
): Promise<FlatJoinSplitV2OperationResult> {
  if (usesPrivateFees())
    return executePrivateFeeSpend({ ...params, amount: 0n }, 2);
  try {
    const pool = requireEnabled();
    const tokenAddress = ethers.getAddress(params.tokenAddress);
    const { provider, chainId } = await requireProvider(params.signer);
    const targetDomain = await readCurrentTokenDomain(
      provider,
      pool,
      tokenAddress,
    );
    const currentTreeIndex = targetDomain.treeIndex;
    if (params.sourceTreeIndex === currentTreeIndex) {
      fail("migration source must be a retired tree");
    }
    let allow: Set<string> | null = null;
    if (params.commitments) {
      if (params.commitments.length === 0 || params.commitments.length > 32) {
        fail("migration commitment selection must contain 1 to 32 entries");
      }
      const normalized = params.commitments.map((value) => {
        try {
          const parsed = BigInt(value);
          if (parsed === 0n) fail("migration commitment cannot be zero");
          return flatFieldHex(parsed).toLowerCase();
        } catch {
          return fail("migration commitment is not a canonical field element");
        }
      });
      allow = new Set(normalized);
      if (allow.size !== normalized.length) {
        fail("migration commitment selection contains duplicates");
      }
    }
    const available = (
      await authenticateAndDedupeCandidates({
        candidates: candidateNotes(params.notes, pool, chainId, tokenAddress),
        pool,
        chainId,
        tokenAddress,
        spendingKeys: params.spendingKeys,
      })
    ).filter(
      ({ note }) =>
        note.treeIndex === params.sourceTreeIndex &&
        (!allow || allow.has(note.commitment.toLowerCase())),
    );
    if (available.length === 0)
      fail("no spendable notes were selected for migration");
    if (allow && available.length !== allow.size) {
      fail(
        "one or more requested migration commitments are unavailable or invalid",
      );
    }
    if (available.length > 32)
      fail("migration is limited to 32 notes per proof");
    const value = available.reduce((sum, note) => sum + note.note.value, 0n);
    if (value >= 1n << 120n) fail("migrated aggregate does not fit uint120");
    const output = await createFlatJoinSplitV2Note({
      recipientMpk: params.spendingKeys.mpk,
      chainId,
      poolAddress: pool,
      treeIndex: currentTreeIndex,
      tokenAddress,
      tokenHash: targetDomain.tokenHash,
      value,
    });
    params.onLog(
      `Migrating ${available.length} retired-tree note(s) into tree ${currentTreeIndex}`,
      "info",
    );
    const settled = await proveAndSend({
      ...params,
      provider,
      pool,
      chainId,
      tokenAddress,
      selected: available,
      outputs: [output],
      kind: "migrate",
      expectedOutputTreeIndex: targetDomain.treeIndex,
      expectedOutputTokenHash: targetDomain.tokenHash,
      deliveries: selfDeliveries([output], params.spendingKeys),
    });
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: available.map((note) => note.note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[0],
      journalId: settled.journalId,
    };
  } catch (error) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
